import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail } from 'lucide-react';
import { getDomainConfig } from '@/utils/domainConfig';

// Inquiry option used to prefill the mailto subject and body. `prefillType`
// matches the ?type= value that other pages pass when deep-linking here.
interface InquiryOption {
  value: string;
  label: string;
  prefillType?: string;
  prefillMessage?: string;
}

export function ContactPage() {
  // QSAVE-family domains show QSAVE brand/options; commercial domains show QKey.
  const isQSAVE = getDomainConfig().type === 'opensource';
  const contactEmail = isQSAVE ? 'hello@qsave.org' : 'hello@qkey.co';
  const [searchParams] = useSearchParams();
  const inquiryType = searchParams.get('type');

  // Brand-specific inquiry options. QKey leans commercial (sales, products,
  // integration); QSAVE leans community/mission. Shared options live in both.
  const qkeyInquiryOptions: InquiryOption[] = [
    {
      value: 'enterprise',
      label: 'Enterprise / talk to sales',
      prefillType: 'enterprise',
      prefillMessage: 'I would like to talk to QKey sales about an enterprise engagement.'
    },
    {
      value: 'vault',
      label: 'Quantum Vault (wallet)',
      prefillType: 'vault',
      prefillMessage: 'I have a question about the QKey Quantum Vault wallet.'
    },
    {
      value: 'pqp-integration',
      label: 'PQP two-factor integration',
      prefillType: 'pqp',
      prefillMessage: 'I would like to integrate PQP two-factor spend protection.'
    },
    {
      value: 'exchange-waitlist',
      label: 'PQ Exchange waitlist',
      prefillType: 'waitlist',
      prefillMessage: 'I would like to join the PQ Exchange waitlist.'
    },
    {
      value: 'uspq',
      label: 'USPQ (post-quantum dollar)',
      prefillType: 'uspq',
      prefillMessage: 'I have a question about USPQ.'
    },
    {
      value: 'partner',
      label: 'Partner by product',
      prefillType: 'partner',
      prefillMessage: 'I am interested in partnering with QKey on a specific product.'
    },
    {
      value: 'technical',
      label: 'Technical support / docs',
      prefillType: 'technical',
      prefillMessage: 'I need technical support or integration assistance.'
    },
    { value: 'general', label: 'General question' },
    { value: 'launch-updates', label: 'Join launch updates' }
  ];

  const qsaveInquiryOptions: InquiryOption[] = [
    { value: 'community', label: 'Community / get involved' },
    {
      value: 'recovery',
      label: 'Recovery claim question',
      prefillType: 'recovery',
      prefillMessage: 'I have a question about a recovery claim.'
    },
    {
      value: 'service-pool',
      label: 'Service pools / contribute work',
      prefillType: 'service-pool',
      prefillMessage: 'I would like to contribute to a QSAVE service pool.'
    },
    { value: 'governance', label: 'Governance / participate' },
    {
      value: 'partner',
      label: 'Partnership / collaboration',
      prefillType: 'partner',
      prefillMessage: 'I am interested in collaborating with QSAVE.'
    },
    { value: 'media', label: 'Media / press' },
    { value: 'general', label: 'General question' }
  ];

  const inquiryOptions = isQSAVE ? qsaveInquiryOptions : qkeyInquiryOptions;

  // Resolve an incoming ?type= to a dropdown option (and its prefilled message).
  const matchedOption = inquiryType
    ? inquiryOptions.find(opt => opt.prefillType === inquiryType)
    : undefined;

  // A ?type= deep link preselects the subject and opening line; without one the
  // reader still gets a usable subject.
  const subject = matchedOption?.label ?? (isQSAVE ? 'QSAVE enquiry' : 'QKey enquiry');
  const body = matchedOption?.prefillMessage ?? '';

  const mailtoHref =
    `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}` +
    (body ? `&body=${encodeURIComponent(body)}` : '');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {isQSAVE ? 'Get involved' : 'Talk to us'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {isQSAVE
                  ? 'QSAVE is a mission-led protocol and charitable Foundation, governed by people. Whether you want to follow the work, contribute to a service pool, ask about a recovery claim, or help govern it, we would like to hear from you.'
                  : 'QKey is the factory for quantum-safe Bitcoin. Whether you are an enterprise planning a migration, a builder integrating PQP, or evaluating Quantum Vault, the PQ Exchange or USPQ, tell us what you are building and our team will get back to you.'}
              </p>
            </div>

            {/* Contact Form Card */}
            <Card className="shadow-xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <CardHeader className="text-center pb-6">
                <div className="w-16 h-16 bg-quantum-100 dark:bg-quantum-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-quantum-600 dark:text-quantum-400" />
                </div>
                <CardTitle className="text-2xl text-gray-900 dark:text-white">
                  {isQSAVE ? 'Reach the project' : 'Tell us what you need'}
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-300">
                  {isQSAVE
                    ? 'Email the project directly and we will route your message to the right people.'
                    : 'Email the team directly. For enterprise engagements, say so in the subject line and we will route it to sales.'}
                </CardDescription>
              </CardHeader>

              <CardContent className="text-center pb-8">
                <a
                  href={mailtoHref}
                  className="text-2xl font-semibold text-quantum-600 hover:text-quantum-700 dark:text-quantum-400 dark:hover:text-quantum-300 break-all"
                >
                  {contactEmail}
                </a>

                <div className="mt-6">
                  <a href={mailtoHref} className="quantum-button inline-flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    Open your email app
                  </a>
                </div>

                {matchedOption && (
                  <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    Your message will be prefilled about <strong>{matchedOption.label}</strong>.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Additional Contact Info */}
            <div className="mt-12 text-center">
              <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  {isQSAVE ? 'Other ways to connect' : 'Other ways to reach us'}
                </h3>
                <div className="space-y-2 text-gray-600 dark:text-gray-300">
                  {isQSAVE ? (
                    <>
                      <p>Community chat: <strong>[TBD]</strong></p>
                      <p>Discussion forum: <strong>[TBD]</strong></p>
                      <p>Governance (Snapshot): <strong>[TBD]</strong></p>
                      <p className="text-sm">
                        Service-pool participants are compensated for performed and validated work.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        Enterprise migration engagements: see the{' '}
                        <a href="/enterprise" className="text-quantum-600 hover:underline">
                          enterprise page
                        </a>
                        .
                      </p>
                      <p className="text-sm">
                        Lost or at-risk coins, recovery claims and the QSAVE registry are handled by the QSAVE
                        Foundation at{' '}
                        <a
                          href="https://qsave.org"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-quantum-600 hover:underline"
                        >
                          qsave.org
                        </a>
                        .
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default ContactPage;